import { UseFormSetValue } from "react-hook-form";
import { customerApi, IItemIdRequest, IPafApplication, IUser, userApi } from "../../_api";
import { useTheme } from "@mui/material";
import { useAuth } from "../../_auth/useAuth";
import { useCallback, useState } from "react";
import { Search } from "@mui/icons-material";
import { CustomerAddDialog, DaButton, IAutocompleteDialogTextProps, IMessagePanelProps, MsgBoxType, MuiGridDataProp } from "../_shared";

export type CompanySelectionProps = {
    setValue: UseFormSetValue<IPafApplication>;
    handleMsgPanelProps?: (props: IMessagePanelProps) => void;
    handleError?: (show: boolean) => void;
    handleMsgBoxProps?: (props: IMessagePanelProps) => void;
    handleMsgBoxOpen?: (open: boolean) => void;
    naicsSearch?: (search_term: string) => void;
    stateSearch?: (search_term: string) => void;
    setAuthorizedUsers?: (users: IUser[]) => void;
}

export const CompanySelection = (
    {
        setValue, handleMsgPanelProps, handleError, handleMsgBoxProps, handleMsgBoxOpen, naicsSearch, stateSearch, setAuthorizedUsers
    }: CompanySelectionProps
) => {
    const theme = useTheme();
    const { getAccessToken } = useAuth();
    const [dlgAddCompanyOpen, setDlgAddCompanyOpen] = useState<boolean>(false);
    const [dlgAddCompanyProps, setDlgAddCompanyProps] = useState<IAutocompleteDialogTextProps>({ title: "Choose Authorized Company" });
    const [isCompaniesLoading, setIsCompaniesLoading] = useState<boolean>(true);
    let userId: number = -1;
    const showErrorPanel = ({ title, message, type = "error" }: IMessagePanelProps) => {
        const msgProps: IMessagePanelProps = {
            message: message,
            title: title,
            type: type
        }
        handleMsgPanelProps?.(msgProps);
        handleError?.(true);
    }
    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        const msgProps: IMessagePanelProps = {
            message: message,
            title: title,
            type: type
        }
        handleMsgBoxProps?.(msgProps);
        handleMsgBoxOpen?.(true);
    }
    const onClickCompanyAdd = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        // Display Add Company dialog
        dlgAddCompany_Show("Choose Authorized Company");
    }, []);
    const dlgAddCompany_Show = (title?: string) => {
        setDlgAddCompanyProps({
            title: title,
        })
        setDlgAddCompanyOpen(true);
    }
    const dlgAddCompany_Close = (value?: string, item_id?: number) => {
        setDlgAddCompanyOpen(false);
        if (value && value === "ok" && item_id) {
            // Get company data and populate form
            getUserCompanyData(item_id);
        }
    }
    const addCompanyAuth = useCallback(async (options): Promise<void> => {
        setIsCompaniesLoading(true);
        const auth_token = await getAccessToken();
        let apiPromise = userApi.customers_add(options, auth_token);

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.success) {
                getUserCompanyData(options.item_id);
            } else {
                msgBox_Show(
                    "An error occurred while adding Authorized Companies... " +
                    "please retry you last action. If you continue to experience issues, " +
                    "contact customer support.",
                    "Unable to Add Authorized Companies...",
                    "warning"
                );
            }
        })
            .catch(() => {
                msgBox_Show(
                    "An error occurred while adding Authorized Companies data. " +
                    "There may have been a network error.",
                    "User API Error",
                    "warning"
                );
            })
            .finally(() => {
                setIsCompaniesLoading(false);
            });
    }, []);
    const getUserCompanyData = useCallback(async (customerId): Promise<void> => {
        setIsCompaniesLoading(true);
        const options: IItemIdRequest = { id: customerId as number };
        const auth_token = await getAccessToken();

        customerApi.get_by_id(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        if (resp.data) {
                            const customer = resp.data;
                            // Populate form fields with customer data
                            setValue("customer_id", customer.customer_id);
                            setValue("company__company_name", customer.company_name, { shouldValidate: true, shouldDirty: true });
                            setValue("company__address1", customer.address1, { shouldValidate: true, shouldDirty: true });
                            setValue("company__address2", customer.address2 || "", { shouldValidate: true, shouldDirty: true });
                            setValue("company__city", customer.city, { shouldValidate: true, shouldDirty: true });
                            setValue("company__state", customer.state, { shouldValidate: true, shouldDirty: true });
                            setValue("company__postal_code", customer.postal_code, { shouldValidate: true, shouldDirty: true });
                            setValue("company__federal_tax_id", customer.federal_tax_id || "", { shouldValidate: true, shouldDirty: true });
                            setValue("company__naics_code", customer.naics_code, { shouldValidate: true, shouldDirty: true });
                            setValue("company__company_registration_id", customer.company_registration_id || "", { shouldValidate: true, shouldDirty: true });
                            setValue("company__phone", customer.phone, { shouldValidate: true, shouldDirty: true });
                            setValue("company__representative_name", customer.representative_name, { shouldValidate: true, shouldDirty: true });
                            setValue("company__representative_title", customer.representative_title || "", { shouldValidate: true, shouldDirty: true });
                            setValue("company__representative_email", customer.representative_email, { shouldValidate: true, shouldDirty: true });
                            setValue("company__company_website", customer.company_website || "", { shouldValidate: true, shouldDirty: true });
                            setValue("company__dba_name" as any, customer.dba_name || "", { shouldValidate: true, shouldDirty: true });
                            setValue("company__parent_company_name" as any, customer.parent_company_name || "", { shouldValidate: true, shouldDirty: true });
                            setValue("is_foreign_location", customer.is_foreign_location ?? false, { shouldValidate: true, shouldDirty: true });
                            setValue("is_ncoa", customer.is_ncoa ?? false, { shouldValidate: true, shouldDirty: true });
                            setValue("is_dsf", customer.is_dsf ?? false, { shouldValidate: true, shouldDirty: true });
                            setValue("is_triplex", customer.is_triplex ?? false, { shouldValidate: true, shouldDirty: true });
                            setValue("is_waiver_on_file", customer.is_waiver_on_file ?? false, { shouldValidate: true, shouldDirty: true });
                            setValue("company__is_internal" as any, customer.is_internal ?? false, { shouldValidate: true, shouldDirty: true });
                            setValue("paf_sid", customer.company_sid, { shouldValidate: true, shouldDirty: true });
                            
                            if (naicsSearch && customer.naics_code) {
                                naicsSearch(customer.naics_code);
                            }
                            if (stateSearch && customer.state) {
                                stateSearch(customer.state);
                            }
                            if(customer.authorized_users.length > 0) {
                                setAuthorizedUsers?.(customer.authorized_users);
                            }
                        }
                    } else if (resp.error && resp.error.length > 0) {
                        showErrorPanel({
                            title: "Authorized Companies Not Found Error",
                            message: "An error occurred while retrieving Authorized Companies data. " +
                                "The Authorized Companies data was not found.",
                        });
                    }
                }
            })
            .catch(() => {
                showErrorPanel({
                    title: "User API Fetch Error",
                    message: "An error occurred while fetching Authorized Companies data. " +
                        "There may have been a network error.",
                });
            })
            .finally(() => {
                setIsCompaniesLoading(false);
            });
    }, [userId, setValue]);


    return (
        <>
            <DaButton
                buttonStyle="primary"
                startIcon={<Search />}
                onClick={onClickCompanyAdd}
                sx={{ width: theme.spacing(25), ml: 1 }}
            >Select Company</DaButton>
            <CustomerAddDialog
                isOpen={dlgAddCompanyOpen}
                onClose={dlgAddCompany_Close}
                userId={userId}
                {...dlgAddCompanyProps}
            />
        </>
    );
}
